import type { AgentConnectionBinding } from './connection-binding'

export type AvatarSetupBinding = AgentConnectionBinding

export interface AvatarSetupCompletion {
  agentId?: string
  language?: string
  realtimeProvider?: string
  avatarId?: string
  avatarType?: string
  voiceId?: string
}

export type AvatarSetupRequestStatus =
  | 'sent'
  | 'already_sent'
  | 'already_complete'
  | 'retryable'
  | 'exhausted'
  | 'stale'

export interface AvatarSetupRequestResult {
  status: AvatarSetupRequestStatus
  attempts: number
}

interface AvatarSetupRequest<TApi extends object> {
  api: TApi
  binding: AvatarSetupBinding
  requiresToken: boolean
  fetchToken: () => Promise<string | undefined>
  sendSetup: (token: string | undefined) => boolean
}

interface ActiveSetup<TApi extends object> {
  api: TApi
  binding: AvatarSetupBinding
  key: string
  generation: number
  attempts: number
  sent: boolean
  completed: boolean
  inFlight: Promise<AvatarSetupRequestResult> | null
}

interface AvatarSetupCoordinatorConfig {
  maxAttempts?: number
}

const bindingKey = (binding: AvatarSetupBinding): string => JSON.stringify([
  binding.agentId,
  binding.language,
  binding.realtimeProvider,
  binding.avatarId,
  binding.avatarType,
  binding.voiceId,
])

/**
 * Owns avatar_setup concurrency across React effects and reconnects.
 * A request is scoped to the concrete API object plus its complete setup binding.
 */
export class AvatarSetupCoordinator<TApi extends object> {
  private readonly maxAttempts: number
  private generation = 0
  private active: ActiveSetup<TApi> | null = null

  constructor(config: AvatarSetupCoordinatorConfig = {}) {
    this.maxAttempts = config.maxAttempts ?? 3
  }

  request(request: AvatarSetupRequest<TApi>): Promise<AvatarSetupRequestResult> {
    const active = this.activate(request.api, request.binding)

    if (active.completed) {
      return Promise.resolve({ status: 'already_complete', attempts: active.attempts })
    }
    if (active.sent) {
      return Promise.resolve({ status: 'already_sent', attempts: active.attempts })
    }
    if (active.inFlight) {
      return active.inFlight
    }
    if (active.attempts >= this.maxAttempts) {
      return Promise.resolve({ status: 'exhausted', attempts: active.attempts })
    }

    active.attempts += 1
    const requestGeneration = active.generation

    const pending = (async (): Promise<AvatarSetupRequestResult> => {
      let token: string | undefined
      try {
        token = await request.fetchToken()
      } catch {
        return this.resultFor(request.api, requestGeneration, 'retryable')
      }

      if (!this.isCurrent(request.api, requestGeneration)) {
        return { status: 'stale', attempts: active.attempts }
      }
      if (request.requiresToken && !token) {
        return { status: 'retryable', attempts: active.attempts }
      }

      let sent = false
      try {
        sent = request.sendSetup(token)
      } catch {
        sent = false
      }

      if (!this.isCurrent(request.api, requestGeneration)) {
        return { status: 'stale', attempts: active.attempts }
      }
      if (!sent) {
        return { status: 'retryable', attempts: active.attempts }
      }

      active.sent = true
      return { status: 'sent', attempts: active.attempts }
    })()

    active.inFlight = pending
    void pending.then(
      () => this.clearInFlight(active, pending),
      () => this.clearInFlight(active, pending),
    )
    return pending
  }

  complete(api: TApi, completion: AvatarSetupCompletion): boolean {
    const active = this.active
    if (!active || active.api !== api || !active.sent) return false
    if (!completion.agentId || completion.agentId !== active.binding.agentId) return false
    if (!completion.language || completion.language !== active.binding.language) return false
    if (
      !completion.realtimeProvider
      || completion.realtimeProvider !== active.binding.realtimeProvider
    ) return false
    if (!completion.avatarId || completion.avatarId !== active.binding.avatarId) return false
    if (!completion.avatarType || completion.avatarType !== active.binding.avatarType) return false
    if (!completion.voiceId || completion.voiceId !== active.binding.voiceId) return false

    active.completed = true
    active.attempts = 0
    return true
  }

  invalidate(api?: TApi): boolean {
    const active = this.active
    if (!active || (api && active.api !== api)) return false

    const resetAttemptBudget = active.completed
    active.generation = ++this.generation
    if (resetAttemptBudget) active.attempts = 0
    active.sent = false
    active.completed = false
    active.inFlight = null
    return true
  }

  canRetry(api: TApi, binding: AvatarSetupBinding): boolean {
    const active = this.active
    return Boolean(
      active
      && active.api === api
      && active.key === bindingKey(binding)
      && !active.sent
      && !active.completed
      && active.attempts < this.maxAttempts
    )
  }

  snapshot() {
    const active = this.active
    return {
      binding: active?.binding ?? null,
      attempts: active?.attempts ?? 0,
      sent: active?.sent ?? false,
      completed: active?.completed ?? false,
      inFlight: Boolean(active?.inFlight),
    }
  }

  private activate(api: TApi, binding: AvatarSetupBinding): ActiveSetup<TApi> {
    const key = bindingKey(binding)
    if (this.active?.api === api && this.active.key === key) {
      return this.active
    }

    this.active = {
      api,
      binding: { ...binding },
      key,
      generation: ++this.generation,
      attempts: 0,
      sent: false,
      completed: false,
      inFlight: null,
    }
    return this.active
  }

  private isCurrent(api: TApi, generation: number): boolean {
    return this.active?.api === api && this.active.generation === generation
  }

  private resultFor(
    api: TApi,
    generation: number,
    currentStatus: AvatarSetupRequestStatus,
  ): AvatarSetupRequestResult {
    if (!this.isCurrent(api, generation)) {
      return { status: 'stale', attempts: 0 }
    }
    return { status: currentStatus, attempts: this.active?.attempts ?? 0 }
  }

  private clearInFlight(
    active: ActiveSetup<TApi>,
    pending: Promise<AvatarSetupRequestResult>,
  ) {
    if (this.active === active && active.inFlight === pending) {
      active.inFlight = null
    }
  }
}

export const avatarSetupCoordinator = new AvatarSetupCoordinator<object>({ maxAttempts: 3 })
